<template>
  <ScreenWidthProvider
    v-slot="{ isWider }"
    class="tab-pane product-content__hotel-list"
    role="tabpanel"
  >
    <div class="mb-12 w-full">
      <div
        v-if="loading"
        class="text-center"
      >
        <div class="py-4">
          <div class="spinner-grow text-primary" />
        </div>
      </div>
      <template v-else>
        <LazyIconDropdown
          v-if="allBedplaces.length > 1 && selectDateOptions?.length"
          class="mt-12"
          :options="selectDateOptions"
          icon="calendar"
          :value="selectedDate"
          @on-select="selectDate"
        >
          <template #placeholder>
            <span>
              <span data-i18n="tripPage.hotels.selectDateDropdownPlaceholder">
                {{ $t('tripPage.hotels.selectDateDropdownPlaceholder') }}&nbsp;
              </span>
              <span
                class="lowercase"
                :data-i18n="bedplacePlaceholderType"
              >{{ $t(bedplacePlaceholderType) }}</span>
            </span>
          </template>
        </LazyIconDropdown>
        <template v-if="!shouldShowSingleFullBedplace">
          <div
            v-for="hotel in filteredProcessedHotels"
            :key="hotel.url_name"
            class="mt-12 pb-1"
          >
            <div class="flex items-center justify-center md:justify-start mb-6">
              <h3 class="product-content-bedplace-title mb-0">
                <span class="mr-2">
                  {{ hotel.propertyName }}
                </span>
                <Stars
                  v-if="hotel.rating || hotel.category"
                  :score="hotel.rating || hotel.category"
                  :size="isWider('md') ? 'lg' : 'sm'"
                />
              </h3>
            </div>
            <LazyHotelPreview
              class="mb-12"
              :hotel="hotel"
              show-link-to-hotel
            />
          </div>
          <div
            v-for="ship in filteredProcessedShips"
            :key="ship.url_name"
            class="mt-12 pb-1"
          >
            <div class="flex items-center justify-center md:justify-start mb-6">
              <h3 class="product-content-bedplace-title mb-0">
                <span class="mr-2">
                  {{ ship.name }}
                </span>
                <Stars
                  v-if="ship.rating"
                  class="ml-4"
                  :score="ship.rating"
                  :size="isWider('md') ? 'lg' : 'sm'"
                />
              </h3>
            </div>
            <LazyShipPreview
              :key="ship.url_name"
              class="mb-12"
              :ship="ship"
              show-link-to-ship
            />
          </div>
        </template>
        <LazyBedplaceShip
          v-else-if="filteredProcessedShips.length"
          :key="filteredProcessedShips[0]?.url_name"
          class="mt-12"
          :ship="filteredProcessedShips[0]"
        />
        <LazyBedplaceHotel
          v-else-if="filteredProcessedHotels.length"
          :key="filteredProcessedHotels[0]?.url_name"
          class="mt-2"
          :hotel="filteredProcessedHotels[0]"
        />
      </template>
    </div>
  </ScreenWidthProvider>
</template>

<script>
import { mapState } from 'pinia'

export default defineNuxtComponent({
  props: {
    hotels: {
      type: Array,
      required: true,
    },

    cruiseships: {
      type: Array,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      selectedDate: null,
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['locale']),

    processedHotels () {
      return (this.hotels || [])
        .map(hotel => processHotelInfo(hotel))
    },

    processedShips () {
      return (this.cruiseships || [])
        .map(ship => processShipInfo(ship))
    },

    allBedplaces () {
      return [
        ...this.processedHotels,
        ...this.processedShips,
      ]
    },

    filteredProcessedHotels () {
      return this.processedHotels
        .filter(this.isAvailableSelectedDate)
    },

    filteredProcessedShips () {
      return this.processedShips
        .filter(this.isAvailableSelectedDate)
    },

    availableDates () {
      return [...new Set(this.allBedplaces.map(({ dates }) => dates).flat())]
        .sort((a, b) => a > b ? 1 : -1)
    },

    selectDateOptions () {
      const dateLocale = localeToLang(this.locale)

      return this.availableDates.map((date) => {
        const hotelWithDateCount = this.allBedplaces.filter(({ dates }) => dates.includes(date))?.length
        const hotelWithDateText = hotelWithDateCount
          ? `${hotelWithDateCount} ${this.$t('hotelHits')}`
          : null

        return {
          text: new Date(date).toLocaleDateString(dateLocale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          endText: hotelWithDateText,
          value: date,
          id: date,
        }
      })
    },

    bedplacePlaceholderType () {
      return this.hotels?.length ? 'hotel' : 'ship'
    },

    shouldShowSingleFullBedplace () {
      return (
        (
          !this.filteredProcessedHotels.length &&
          this.filteredProcessedShips.length === 1 &&
          this.filteredProcessedShips[0]?.images?.length // Full single ships look very bad without images
        ) || (
          !this.filteredProcessedShips.length &&
          this.filteredProcessedHotels.length === 1
        )
      )
    },
  },

  methods: {
    selectDate (date) {
      this.selectedDate = date
    },

    isAvailableSelectedDate (bedplace) {
      return (
        !this.selectedDate ||
        !bedplace.dates ||
        bedplace.dates.includes(this.selectedDate.value)
      )
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-hotel-list";

.product-content-bedplace-title {
  @media (max-width: theme('screens.md')) {
    font-size: 1.3rem;
  }
}
</style>
